import { Chip } from '@liveauctioneers/hammer-ui-core/chips';
import { ComponentProps, MouseEvent, useCallback, useMemo } from 'react';
import { ConditionalMenuContainer } from '../ConditionalMenuContainer/ConditionalMenuContainer';
import { CurrentOpenMenu } from '@/pages/SearchPageSimplified/types';
import { Distance } from '@/types/Distance';
import { FacetOption } from '@/types/FacetOption';
import { FilterAction } from '@/types/SearchFacet';
import { getMergedOptionFacet } from '@/redux/modules/searchFacetAggregates.selectors';
import { getRoundedNumberText } from '@/pages/SearchPageSimplified/utils/getRoundedNumberText';
import { LocationRangeFilter } from './components/LocationRangeFilter/LocationRangeFilter';
import {
    modifyParamsRemoveCountryAndDistance,
    modifyParamsRemovePage,
    modifyParamsReplaceCountry,
    parseQueryParams,
} from '@/utils/queryParams';
import { QuickFilterName, useTrackQuickFilterPillClicked } from '@/utils/amplitude/analytics/searchPage';
import { Radio } from '@liveauctioneers/hammer-ui-core/radio';
import { useAppSelector } from '@/redux/hooks';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useLocation } from 'react-router-dom';
import { useNavigateSearchPageWithParams } from '@/pages/SearchPageSimplified/hooks/useNavigateSearchPageWithParams';
import { ViewMore } from '@liveauctioneers/hammer-ui-core/viewMore';
import FacetTypes from '@/types/search/enums/facetTypes';

type Props = {
    currentOpenMenu?: CurrentOpenMenu;
    distance: Distance;
    isAllFilters?: boolean;
    isArchivedSearch: boolean;
    onMenuClose: () => void;
    onMenuOpen: (currentMenu: CurrentOpenMenu) => void;
};

export const LocationFilter = ({
    currentOpenMenu,
    distance,
    isAllFilters,
    isArchivedSearch,
    onMenuClose,
    onMenuOpen,
}: Props) => {
    const { formatMessage } = useIntl();
    const trackQuickFilterPillClicked = useTrackQuickFilterPillClicked();
    const locationFacet = useAppSelector((state) =>
        getMergedOptionFacet(state, isArchivedSearch, FacetTypes.COUNTRY_CODE_ID)
    );
    const { options = [] } = locationFacet;
    const { search } = useLocation();

    const queryParams = parseQueryParams(search);
    const countryFacetParam = queryParams.country;
    const navigateWithParams = useNavigateSearchPageWithParams();

    const handleMenuOpen: ComponentProps<typeof ConditionalMenuContainer>['onMenuOpen'] = useCallback(
        (menu) => {
            if (!isAllFilters) {
                trackQuickFilterPillClicked(QuickFilterName.Location);
            }
            onMenuOpen(menu);
        },
        [isAllFilters, onMenuOpen, trackQuickFilterPillClicked]
    );

    const {
        selectedCountryFacet,
        selectedDistanceFacetName,
    }: { selectedCountryFacet: FacetOption | undefined; selectedDistanceFacetName: string | undefined } =
        useMemo(() => {
            const selectedCountryFacet = options.find((option) => option.id === countryFacetParam);
            const selectedDistanceFacetName = distance.zipCode
                ? `Within ${distance.space} miles of ${distance.zipCode}`
                : undefined;

            return {
                selectedCountryFacet: selectedCountryFacet,
                selectedDistanceFacetName: selectedDistanceFacetName ?? selectedCountryFacet?.name ?? undefined,
            };
        }, [countryFacetParam, distance.space, distance.zipCode, options]);

    const onCountryOptionClick = (option?: FacetOption) => {
        const keepMenuOpenValue = isAllFilters ? CurrentOpenMenu.AllFilters : CurrentOpenMenu.Location;

        if (!option) {
            let newParams = modifyParamsRemoveCountryAndDistance(queryParams);
            newParams = modifyParamsRemovePage(newParams);
            navigateWithParams({
                checkForPageRemoval: true,
                currentOpenMenu: keepMenuOpenValue,
                filterAction: FilterAction.LocationRemoved,
                params: newParams,
                preventScrollReset: true,
            });
        } else {
            let newParams = modifyParamsReplaceCountry(queryParams, option.id);
            newParams = modifyParamsRemovePage(newParams);
            navigateWithParams({
                checkForPageRemoval: true,
                currentOpenMenu: keepMenuOpenValue,
                filterAction: FilterAction.LocationChanged,
                params: newParams,
                preventScrollReset: true,
            });
        }
    };

    const onRemoveChipCountryAndDistanceValue = (event: MouseEvent<HTMLInputElement>) => {
        event.preventDefault();
        let newParams = modifyParamsRemoveCountryAndDistance(queryParams);
        newParams = modifyParamsRemovePage(newParams);
        navigateWithParams({
            checkForPageRemoval: true,
            filterAction: FilterAction.LocationRemoved,
            params: newParams,
            preventScrollReset: true,
        });
    };

    const formattedOptions = useMemo(() => {
        return options.filter((option) => option.count !== 0).sort((a, b) => b.count - a.count);
    }, [options]);

    return (
        <ConditionalMenuContainer
            accordionDefaultOpen
            currentOpenMenu={currentOpenMenu}
            currentOpenMenuId={CurrentOpenMenu.Location}
            isAllFilters={isAllFilters}
            onMenuClose={onMenuClose}
            onMenuOpen={handleMenuOpen}
            title={formatMessage({ id: 'searchPage.facets.itemLocation' })}
            trigger={
                <Chip
                    onClose={onRemoveChipCountryAndDistanceValue}
                    selected={Boolean(selectedDistanceFacetName)}
                    style="singleFilter"
                >
                    {selectedDistanceFacetName || 'Item Location'}
                </Chip>
            }
        >
            <Radio.Option
                checked={!countryFacetParam}
                onChange={() => onCountryOptionClick()}
            >
                {formatMessage({ id: 'searchPage.facets.anywhere' })}
            </Radio.Option>
            {distance.zipCode && selectedCountryFacet?.count === 0 && (
                <Radio.Option defaultChecked>{selectedCountryFacet?.name}</Radio.Option>
            )}

            <ViewMore
                collapseText="View Less"
                expandText="View More"
                fadeBackgroundColor="#fff"
            >
                <div className="flex flex-col gap-xs">
                    {formattedOptions.map((option) => (
                        <Radio.Option
                            defaultChecked={option.selected}
                            key={option.id}
                            onChange={() => onCountryOptionClick(option)}
                        >
                            {option.name}&nbsp;
                            <span className="text-text-secondary">
                                ({getRoundedNumberText({ totalFound: option.count })})
                            </span>
                        </Radio.Option>
                    ))}
                </div>
            </ViewMore>

            {/* ONLY SHOW IF COUNTRY IS EMPTY OR US, AND NOT ARCHIVED (zip/distance not supported for Past Lots) */}
            {(!countryFacetParam || countryFacetParam === 'US') && !isArchivedSearch && (
                <LocationRangeFilter
                    distance={distance}
                    isAllFilters={isAllFilters}
                />
            )}
        </ConditionalMenuContainer>
    );
};
